import { useEffect, useState } from 'react';
import { useLoaderData, useLocation, useNavigate, useRevalidator, useSearchParams } from 'react-router';
import type { LoaderFunctionArgs } from 'react-router';

import { Button, Icon } from '@ds/core';
import { DataTable, FilterBar } from '@ds/data';
import type { RowId } from '@ds/data';
import { SearchField } from '@ds/forms';
import { Menu, Pagination, Tabs } from '@ds/navigation';
import type { MenuItem } from '@ds/navigation';
import type { Destinatario } from '@/comuns/mensagens';
import { PageHeader } from '@/comuns/PageHeader';
import { useAviso } from '@/comuns/toast';
import { useConfirmacao } from '@/comuns/useConfirmacao';
import { dados } from '@/data/porta';
import type { CargaDeContatos, Fornecedor, Lead, MembroDaEquipe, PrestadorDeServico, TipoDeContato, TipoImportavel } from '@/data/tipos';
import { FornecedorModal } from './estoque/Cadastros';
import { PrestadorModal } from './contatos/CadastroDeTerceiros';
import { AcaoEmLoteModal } from './contatos/AcaoEmLoteModal';
import type { AcaoEmLote } from './contatos/AcaoEmLoteModal';
import { colunasDeFornecedores, colunasDeLeads, colunasDeMembros, colunasDePacientes, colunasDePrestadores } from './contatos/colunas';
import { ExportarContatos } from './contatos/ExportarContatos';
import { DetalheDoLead } from './contatos/DetalheDoLead';
import { DetalheDoMembro } from './contatos/DetalheDoMembro';
import { ImportarContatosModal } from './contatos/ImportarContatosModal';
import { MensagemEmLoteModal } from './contatos/MensagemEmLoteModal';
import { NovoContatoModal } from './contatos/NovoContatoModal';

const POR_PAGINA = 8;

/** A aba é a subrota, não estado: o link de "leads" tem de abrir em leads. */
export function tipoDaRota(pathname: string): TipoDeContato {
    if (pathname.endsWith('/leads')) return 'lead';
    if (pathname.endsWith('/profissionais')) return 'profissional';
    if (pathname.endsWith('/fornecedores')) return 'fornecedor';
    if (pathname.endsWith('/prestadores')) return 'prestador';
    return 'paciente';
}

function tipoDoParametro(valor: string | null): TipoDeContato {
    return valor === 'lead' || valor === 'profissional' || valor === 'fornecedor' || valor === 'prestador' ? valor : 'paciente';
}

/** Fornecedor e prestador não entram por planilha: a importação volta para pacientes. */
function importavel(tipo: TipoDeContato): TipoImportavel {
    return tipo === 'lead' || tipo === 'profissional' ? tipo : 'paciente';
}

/** Os contatos com quem a clínica conversa pelos canais, e que a planilha importa. */
const DE_ATENDIMENTO = (tipo: TipoDeContato) => tipo === 'paciente' || tipo === 'lead';

export async function carregarContatos({ request }: LoaderFunctionArgs): Promise<CargaDeContatos> {
    const url = new URL(request.url);
    return dados().contatos(tipoDaRota(url.pathname), {
        busca: url.searchParams.get('q') ?? undefined,
        pagina: Number(url.searchParams.get('pagina') ?? '1') || 1,
        porPagina: POR_PAGINA,
    });
}

const DESCRICAO: Record<TipoDeContato, (total: number) => string> = {
    paciente: (t) => `${t} ${t === 1 ? 'paciente cadastrado' : 'pacientes cadastrados'}`,
    lead: (t) => `${t} ${t === 1 ? 'lead' : 'leads'} no pipeline comercial`,
    profissional: (t) => `${t} ${t === 1 ? 'profissional' : 'profissionais'} e membros da equipe`,
    fornecedor: (t) => `${t} ${t === 1 ? 'fornecedor' : 'fornecedores'} · o mesmo cadastro de Compras`,
    prestador: (t) => `${t} ${t === 1 ? 'prestador' : 'prestadores'} de serviços`,
};

const TOTAL: Record<TipoDeContato, keyof CargaDeContatos['totais']> = {
    paciente: 'pacientes', lead: 'leads', profissional: 'profissionais', fornecedor: 'fornecedores', prestador: 'prestadores',
};

const NOVO: Record<TipoDeContato, string> = {
    paciente: 'Novo paciente', lead: 'Novo lead', profissional: 'Cadastrar profissional',
    fornecedor: 'Novo fornecedor', prestador: 'Novo prestador',
};

const CAMINHO: Record<TipoDeContato, string> = {
    paciente: 'pacientes', lead: 'leads', profissional: 'profissionais', fornecedor: 'fornecedores', prestador: 'prestadores',
};

/* As ações em lote. Todas diziam "Ação em lote registrada" e nenhuma fazia
   nada; as que dependem do que ainda não existe aparecem desligadas, com o
   motivo na própria linha. */
const ACOES_EM_LOTE: Record<TipoDeContato, MenuItem[]> = {
    paciente: [
        { id: 'profissional', label: 'Trocar profissional', icon: 'user-cog' },
        { id: 'etiqueta', label: 'Adicionar etiqueta', icon: 'tag' },
    ],
    lead: [
        { id: 'responsavel', label: 'Trocar responsável', icon: 'user-cog' },
        { id: 'pipeline', label: 'Mover no pipeline', icon: 'git-branch' },
        { id: 'etiqueta', label: 'Adicionar etiqueta', icon: 'tag' },
    ],
    profissional: [
        { id: 'permissoes', label: 'Editar permissões', icon: 'shield', disabled: true, hint: 'uma pessoa por vez' },
        { id: 'convite', label: 'Reenviar convite', icon: 'send', hint: 'só a quem ainda não entrou' },
    ],
    fornecedor: [],
    prestador: [],
};

export function Contatos() {
    const carga = useLoaderData() as CargaDeContatos;
    const [params, setParams] = useSearchParams();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const revalidator = useRevalidator();
    const aviso = useAviso();
    const [confirmar, confirmacao] = useConfirmacao();

    const [selecionados, setSelecionados] = useState<RowId[]>([]);
    const [novoAberto, setNovoAberto] = useState(false);
    const [mensagemPara, setMensagemPara] = useState<Destinatario[] | null>(null);
    const [acaoEmLote, setAcaoEmLote] = useState<AcaoEmLote | null>(null);
    const [leadAberto, setLeadAberto] = useState<{ lead: Lead; editando: boolean } | null>(null);
    const [membroAberto, setMembroAberto] = useState<{ membro: MembroDaEquipe; editando: boolean } | null>(null);
    const [fornecedorAberto, setFornecedorAberto] = useState<Fornecedor | null>(null);
    const [prestadorAberto, setPrestadorAberto] = useState<PrestadorDeServico | null>(null);

    const tipo = carga.tipo;
    const busca = params.get('q') ?? '';
    const pagina = carga.pagina;
    /* A importação lembra de que aba veio: fechar voltava sempre para Pacientes. */
    const tipoDaImportacao = importavel(tipoDoParametro(params.get('tipo')));

    /* O campo de busca tem estado próprio e escreve na URL com atraso. Ligá-lo
       direto ao parâmetro fazia cada tecla dar a volta pelo roteador e pelo
       loader — e, digitando rápido, letra se perdia no caminho. */
    const [textoBusca, setTextoBusca] = useState(busca);
    useEffect(() => {
        setTextoBusca(busca);
    }, [busca]);

    /* Trocar de aba com linhas marcadas exportaria ou mandaria mensagem para
       registros de outra lista — a seleção morre com a aba. */
    useEffect(() => {
        setSelecionados([]);
    }, [tipo, busca, pagina]);

    const trocarParam = (chave: string, valor: string | null) => {
        setParams((atuais) => {
            const proximos = new URLSearchParams(atuais);
            if (valor === null || valor === '') proximos.delete(chave);
            else proximos.set(chave, valor);
            /* Buscar volta para a primeira página: continuar na página 4 de um
               resultado com uma página só mostraria lista vazia. */
            if (chave === 'q') proximos.delete('pagina');
            return proximos;
        }, { replace: true });
    };

    useEffect(() => {
        if (textoBusca === busca) return;
        const t = window.setTimeout(() => trocarParam('q', textoBusca), 250);
        return () => window.clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [textoBusca, busca]);

    const irParaAba = (novaAba: string) => navigate(`/app/contatos/${CAMINHO[tipoDoParametro(novaAba)]}`);

    /** Os registros marcados, com a PESSOA de cada um — é para ela que a mensagem vai. */
    const marcados = (): { id: string; nome: string; contatoId?: string }[] => {
        const ids = new Set(selecionados.map(String));
        if (carga.tipo === 'lead') return carga.itens.filter((l) => ids.has(l.id)).map((l) => ({ id: l.id, nome: l.nome, contatoId: l.contatoId }));
        if (carga.tipo === 'paciente') return carga.itens.filter((p) => ids.has(p.id)).map((p) => ({ id: p.id, nome: p.nome, contatoId: p.id }));
        const itens: { id: string; nome: string }[] = carga.itens;
        return itens.filter((m) => ids.has(m.id)).map((m) => ({ id: m.id, nome: m.nome }));
    };

    const destinatariosMarcados = (): Destinatario[] =>
        marcados().flatMap((m) => (m.contatoId ? [{ contatoId: m.contatoId, nome: m.nome }] : []));

    const marcarPerdido = async (lead: Lead) => {
        const ok = await confirmar({
            titulo: `Marcar ${lead.nome} como perdido?`,
            descricao: 'O lead sai do quadro do funil. Continua na lista e nos relatórios.',
            confirmar: 'Marcar como perdido',
        });
        if (!ok) return;
        try {
            await dados().atualizarLead(lead.id, { situacao: 'perdido' });
            aviso({ title: 'Lead perdido', description: lead.nome, tone: 'warning' });
            revalidator.revalidate();
        } catch (e) {
            aviso({ title: 'Não foi possível marcar como perdido', description: e instanceof Error ? e.message : undefined, tone: 'danger' });
        }
    };

    const desativar = async (membro: MembroDaEquipe) => {
        const ok = await confirmar({
            titulo: `Desativar o acesso de ${membro.nome}?`,
            descricao: 'A pessoa deixa de entrar na Fórmula MED. O cadastro continua na lista, marcado como desativado.',
            confirmar: 'Desativar acesso',
        });
        if (!ok) return;
        try {
            await dados().atualizarMembro(membro.id, { acesso: 'desativado' });
            aviso({ title: 'Acesso desativado', description: membro.nome, tone: 'warning' });
            revalidator.revalidate();
        } catch (e) {
            aviso({ title: 'Não foi possível desativar o acesso', description: e instanceof Error ? e.message : undefined, tone: 'danger' });
        }
    };

    /* O convite em lote vai só a quem ainda não entrou: para quem já tem acesso
       seria e-mail sem propósito. Um de cada vez, e o resumo diz quem falhou. */
    const reenviarConvites = async () => {
        if (carga.tipo !== 'profissional') return;
        const ids = new Set(selecionados.map(String));
        const alvos = carga.itens.filter((m) => ids.has(m.id) && m.acesso === 'convite-pendente');
        const fora = ids.size - alvos.length;
        if (alvos.length === 0) {
            aviso({ title: 'Nenhum convite para reenviar', description: 'Só quem está com convite pendente recebe convite.', tone: 'warning' });
            return;
        }
        const ok = await confirmar({
            titulo: alvos.length === 1 ? `Reenviar o convite para ${alvos[0]?.nome ?? ''}?` : `Reenviar o convite para ${alvos.length} profissionais?`,
            descricao: `Sai um e-mail para cada um${fora > 0 ? `; ${fora} marcado(s) já têm acesso e ficam de fora` : ''}.`,
            confirmar: 'Reenviar convite',
        });
        if (!ok) return;
        const falhas: string[] = [];
        for (const m of alvos) {
            try {
                await dados().convidarMembro(m.id);
            } catch (e) {
                falhas.push(`${m.nome}: ${e instanceof Error ? e.message : 'falhou'}`);
            }
        }
        const enviados = alvos.length - falhas.length;
        aviso(falhas.length === 0
            ? { title: enviados === 1 ? 'Convite enviado' : `${enviados} convites enviados`, description: alvos.map((m) => m.nome).join(', ') }
            : { title: `${enviados} de ${alvos.length} convites enviados`, description: falhas.join(' · '), tone: 'warning' });
        setSelecionados([]);
        revalidator.revalidate();
    };

    const acaoDaLinha = (acao: string, registro: Lead | MembroDaEquipe) => {
        if (acao === 'abrir') {
            if ('etapa' in registro) setLeadAberto({ lead: registro, editando: false });
            else setMembroAberto({ membro: registro, editando: false });
            return;
        }
        if (acao === 'editar') {
            if ('etapa' in registro) setLeadAberto({ lead: registro, editando: true });
            else setMembroAberto({ membro: registro, editando: true });
            return;
        }
        /* O CRM abre o lead pela URL: "Ver no funil" levava ao quadro e parava. */
        if (acao === 'funil') { navigate(`/app/crm?lead=${encodeURIComponent(registro.id)}`); return; }
        if (acao === 'agenda') { navigate(`/app/agenda?prof=${encodeURIComponent(registro.nome)}`); return; }
        if (acao === 'mensagem' && 'contatoId' in registro) { setMensagemPara([{ contatoId: registro.contatoId, nome: registro.nome }]); return; }
        if (acao === 'perdido' && 'etapa' in registro) { void marcarPerdido(registro); return; }
        if (acao === 'desativar' && !('etapa' in registro)) void desativar(registro);
    };

    const lote = marcados();

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <PageHeader
                title="Contatos"
                description={DESCRICAO[tipo](carga.totais[TOTAL[tipo]])}
                actions={
                    <>
                        <ExportarContatos carga={carga} busca={busca} selecionados={selecionados} label="Exportar" size="md" />
                        {(DE_ATENDIMENTO(tipo) || tipo === 'profissional') && (
                            <Button variant="secondary" iconLeft={<Icon name="upload" size={15} />} onClick={() => navigate(`/app/contatos/importar?tipo=${tipo}`)}>Importar</Button>
                        )}
                        <Button iconLeft={<Icon name="plus" size={15} />} onClick={() => setNovoAberto(true)}>{NOVO[tipo]}</Button>
                    </>
                }
            >
                <Tabs
                    ariaLabel="Tipos de contato" activeId={tipo} onChange={irParaAba}
                    tabs={[
                        { id: 'paciente', label: 'Pacientes', count: carga.totais.pacientes },
                        { id: 'lead', label: 'Leads', count: carga.totais.leads },
                        { id: 'profissional', label: 'Profissionais', count: carga.totais.profissionais },
                        { id: 'fornecedor', label: 'Fornecedores', count: carga.totais.fornecedores },
                        { id: 'prestador', label: 'Prestadores de serviço', count: carga.totais.prestadores },
                    ]}
                />
            </PageHeader>

            <FilterBar
                search={
                    <SearchField
                        value={textoBusca} onChange={setTextoBusca} width={280}
                        placeholder={{
                            paciente: 'Buscar por nome ou telefone', lead: 'Buscar lead por nome ou telefone', profissional: 'Buscar profissional',
                            fornecedor: 'Buscar fornecedor por nome, CNPJ ou telefone', prestador: 'Buscar prestador por nome, telefone ou serviço',
                        }[tipo]}
                    />
                }
                resultLabel={`${carga.itens.length} de ${carga.total}`}
                actions={
                    selecionados.length > 0
                        ? (
                            <>
                                {/* Mensagem pelos canais é para paciente e lead: a equipe não
                                    tem conversa de atendimento. */}
                                {DE_ATENDIMENTO(tipo) && (
                                    <Button size="sm" variant="secondary" iconLeft={<Icon name="message-circle" size={14} />} onClick={() => setMensagemPara(destinatariosMarcados())}>
                                        Enviar mensagem ({selecionados.length})
                                    </Button>
                                )}
                                <ExportarContatos carga={carga} busca={busca} selecionados={selecionados} label={`Exportar (${selecionados.length})`} />
                                <Menu
                                    align="right" width={300}
                                    onSelect={(id) => {
                                        if (id === 'limpar') { setSelecionados([]); return; }
                                        if (id === 'profissional' || id === 'responsavel' || id === 'pipeline' || id === 'etiqueta') setAcaoEmLote(id);
                                        if (id === 'convite') void reenviarConvites();
                                    }}
                                    trigger={<Button size="sm" variant="ghost" iconLeft={<Icon name="ellipsis" size={15} />}>Mais</Button>}
                                    items={[...ACOES_EM_LOTE[tipo], { id: 'divisor', label: '', divider: true }, { id: 'limpar', label: 'Limpar seleção', icon: 'x' }]}
                                />
                            </>
                        )
                        : <ExportarContatos carga={carga} busca={busca} selecionados={selecionados} />
                }
            />

            {carga.tipo === 'paciente' && (
                <DataTable
                    selectable selected={selecionados} onSelect={setSelecionados}
                    columns={colunasDePacientes(carga.convenios)} rows={carga.itens} rowKey="id"
                    onRowClick={(p) => navigate(`/app/contatos/${p.id}`)}
                    emptyMessage="Nenhum paciente encontrado."
                />
            )}
            {carga.tipo === 'lead' && (
                <DataTable
                    selectable selected={selecionados} onSelect={setSelecionados}
                    columns={colunasDeLeads((l) => setLeadAberto({ lead: l, editando: true }), acaoDaLinha)}
                    rows={carga.itens} rowKey="id"
                    onRowClick={(l) => setLeadAberto({ lead: l, editando: false })}
                    emptyMessage="Nenhum lead encontrado."
                />
            )}
            {carga.tipo === 'profissional' && (
                <DataTable
                    selectable selected={selecionados} onSelect={setSelecionados}
                    columns={colunasDeMembros((m) => setMembroAberto({ membro: m, editando: true }), acaoDaLinha)}
                    rows={carga.itens} rowKey="id"
                    onRowClick={(m) => setMembroAberto({ membro: m, editando: false })}
                    emptyMessage="Nenhum profissional encontrado."
                />
            )}

            {carga.tipo === 'fornecedor' && (
                <DataTable
                    selectable selected={selecionados} onSelect={setSelecionados}
                    columns={colunasDeFornecedores()} rows={carga.itens} rowKey="id"
                    rowLabel={(f) => f.nome}
                    onRowClick={setFornecedorAberto}
                    emptyMessage="Nenhum fornecedor encontrado."
                />
            )}
            {carga.tipo === 'prestador' && (
                <DataTable
                    selectable selected={selecionados} onSelect={setSelecionados}
                    columns={colunasDePrestadores()} rows={carga.itens} rowKey="id"
                    rowLabel={(p) => p.nome}
                    onRowClick={setPrestadorAberto}
                    emptyMessage="Nenhum prestador de serviços encontrado."
                />
            )}

            <Pagination
                page={pagina} pageCount={carga.paginas} total={carga.total} pageSize={POR_PAGINA}
                onChange={(p) => trocarParam('pagina', String(p))}
            />

            <NovoContatoModal open={novoAberto} tipo={tipo} onClose={() => setNovoAberto(false)} onCriado={() => revalidator.revalidate()} />

            <MensagemEmLoteModal open={mensagemPara !== null} destinatarios={mensagemPara ?? []} onClose={() => setMensagemPara(null)} />

            <AcaoEmLoteModal
                acao={acaoEmLote} ids={lote.map((m) => m.id)} nomes={lote.map((m) => m.nome)}
                onClose={() => setAcaoEmLote(null)}
                onFeito={() => { setSelecionados([]); revalidator.revalidate(); }}
            />

            <ImportarContatosModal
                open={pathname.endsWith('/importar')} tipo={tipoDaImportacao}
                onClose={() => navigate(`/app/contatos/${CAMINHO[tipoDaImportacao]}`)}
                onImportado={() => revalidator.revalidate()}
            />

            <DetalheDoLead
                lead={leadAberto?.lead ?? null} editando={leadAberto?.editando}
                onClose={() => setLeadAberto(null)} onSalvo={() => revalidator.revalidate()}
                onVerNoFunil={(lead) => navigate(`/app/crm?lead=${encodeURIComponent(lead.id)}`)}
            />

            <DetalheDoMembro
                membro={membroAberto?.membro ?? null} editando={membroAberto?.editando}
                onClose={() => setMembroAberto(null)} onSalvo={() => revalidator.revalidate()}
                onVerAgenda={(membro) => navigate(`/app/agenda?prof=${encodeURIComponent(membro.nome)}`)}
            />

            <FornecedorModal alvo={fornecedorAberto} onClose={() => setFornecedorAberto(null)} onSalvo={() => revalidator.revalidate()} />
            <PrestadorModal alvo={prestadorAberto} onClose={() => setPrestadorAberto(null)} onSalvo={() => revalidator.revalidate()} />

            {confirmacao}
        </div>
    );
}
